import React from 'react'
import {css} from '@emotion/react'

import useComponentSize from '../../../../hooks/useComponentSize'

const Wheel = ({
                 selections,
                 onStart,
                 onChange,
                 onFinished,
                 primaryColor = 'black',
                 contrastColor = 'white',
                 buttonText = 'Spin',
                 upDuration = 100,
                 downDuration = 500,
                 ...props
               }) => {
  // register canvas
  const canvasRef = React.useRef(null)
  const {width, height, ready} = useComponentSize(canvasRef)
  const [canvasInitialized, setCanvasInitialized] = React.useState(false)
  React.useEffect(() => {
    if (canvasRef.current && ready) {
      const canvas = canvasRef.current
      canvas.width = width
      canvas.height = height
      canvas.addEventListener('click', spin, false)

      setCanvasInitialized(true)
      return () => canvas.removeEventListener('click', spin)
    }
  }, [canvasRef.current, ready])

  React.useEffect(() => {
    if (canvasInitialized) {
      drawWheel(0)
    }
  }, [canvasInitialized, selections])

  // draw function
  const drawWheel = React.useCallback(rotatedAngle => {
    const canvas = canvasRef.current
    const radius = Math.min(canvas.width / 2, canvas.height / 2) - 10
    const ratio = radius / 290

    const ctx = canvas.getContext('2d')

    const centerX = canvas.width / 2
    const centerY = canvas.height / 2

    // 清空圖片
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // 畫 segment
    let lastAngle = 0
    const totalWeight = selections.map(selection => selection.weight).reduce((a, b) => a + b, 0)
    for (const selection of selections) {
      const startAngle = lastAngle
      const endAngle = startAngle + (Math.PI * 2) / totalWeight * selection.weight

      ctx.save()
      // 畫 segment
      ctx.beginPath()
      ctx.moveTo(centerX, centerY)
      ctx.arc(centerX, centerY, radius, startAngle + rotatedAngle, endAngle + rotatedAngle, false)
      ctx.lineTo(centerX, centerY)
      ctx.closePath()
      ctx.fillStyle = selection.color
      ctx.fill()
      ctx.lineWidth = 1
      ctx.strokeStyle = primaryColor
      ctx.stroke()
      ctx.save()

      // 畫文字
      ctx.translate(centerX, centerY)
      ctx.rotate((startAngle + endAngle) / 2 + rotatedAngle)
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'center'
      ctx.fillStyle = primaryColor
      ctx.font = `bold ${2 * ratio}em Arial`
      ctx.fillText(fitString(ctx, selection.name, radius * 0.7), radius / 2 + 20 * ratio, 0)
      ctx.restore()

      lastAngle = endAngle
    }

    // 畫中心的圓
    ctx.beginPath()
    ctx.arc(centerX, centerY, 50 * ratio, 0, Math.PI * 2, false)
    ctx.closePath()
    ctx.fillStyle = primaryColor
    ctx.fill()
    // 畫圓外的圈
    ctx.lineWidth = 10 * ratio
    ctx.strokeStyle = contrastColor
    ctx.stroke()
    // 畫圓上的三角
    ctx.beginPath()
    ctx.moveTo(centerX + 20 * ratio, centerY - 50 * ratio)
    ctx.lineTo(centerX - 20 * ratio, centerY - 50 * ratio)
    ctx.lineTo(centerX, centerY - 70 * ratio)
    ctx.closePath()
    ctx.fillStyle = contrastColor
    ctx.fill()
    // 畫圓中的文字
    ctx.font = `bold ${1.9 * ratio}em Arial`
    ctx.fillStyle = contrastColor
    ctx.textAlign = 'center'
    ctx.fillText(buttonText, centerX, centerY + 12 * ratio)

    // 畫最外的圈
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, false)
    ctx.closePath()
    ctx.lineWidth = 10 * ratio
    ctx.strokeStyle = primaryColor
    ctx.stroke()
  }, [primaryColor, contrastColor, selections])

  const [spinning, setSpinning] = React.useState(false)
  const spin = React.useCallback(() => {
    setSpinning(true)
    onStart && onStart()
  }, [onStart])

  const [currentRotatedAngle, setCurrentRotatedAngle] = React.useState(0)
  React.useEffect(() => {
    if (spinning) {
      const spinTime = new Date().getTime()
      let handle
      let newRotatedAngle = currentRotatedAngle
      const maxSpeed = 1 + Math.random()

      const render = () => {
        const totalWeight = selections.map(selection => selection.weight).reduce((a, b) => a + b, 0)

        const currentTime = new Date().getTime()
        const upTime = totalWeight * upDuration
        const downTime = totalWeight * downDuration

        // Determine the current phase and progress
        let angleDelta
        if (currentTime - spinTime < upTime) { // 上升階段 (根據比例加速)
          const progress = (currentTime - spinTime) / upTime
          angleDelta = maxSpeed * Math.sin((progress * Math.PI) / 2)
        } else { // 減速階段 (根據比例減速)
          const progress = (currentTime - spinTime - upTime) / downTime
          angleDelta = maxSpeed * Math.sin((progress * Math.PI) / 2 + Math.PI / 2)
        }

        newRotatedAngle = newRotatedAngle + angleDelta
        while (newRotatedAngle >= Math.PI * 2) {
          newRotatedAngle -= Math.PI * 2
        }

        // draw new wheel
        drawWheel(newRotatedAngle)

        // calculate new selection
        let movedNeedle = (Math.PI * 2) * 3 / 4 - newRotatedAngle
        if (movedNeedle < 0) {
          movedNeedle += Math.PI * 2
        }
        let p = Math.ceil(movedNeedle * totalWeight / (Math.PI * 2))

        let i = 0
        for (; i < selections.length; i++) {
          p -= selections[i].weight
          if (p <= 0) {
            break
          }
        }
        const currentSelection = selections[i]
        onChange && onChange(currentSelection)

        const finished = currentTime > spinTime + (upTime + downTime)
        if (finished) {
          setSpinning(false)
          setCurrentRotatedAngle(newRotatedAngle)
          onFinished && onFinished(currentSelection)
          return
        }

        handle = window.requestAnimationFrame(render)
      }

      render()

      return () => {
        window.cancelAnimationFrame(handle)
      }
    }
  }, [drawWheel, spinning, currentRotatedAngle, onChange, onFinished])

  return (
    <div
      css={css`
        height: 100%;
        width: 100%;
        
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `}
    >
      <canvas
        css={css`
          flex: 1;

          cursor: pointer;
          aspect-ratio: 1;
          max-height: 100%;
          max-width: 100%;
        `}
        ref={canvasRef}
        {...props}
      />
    </div>
  )
}

const binarySearch = ({ max, getValue, match }) => {
  let min = 0;

  while (min <= max) {
    let guess = Math.floor((min + max) / 2);
    const compareVal = getValue(guess);

    if (compareVal === match) return guess;
    if (compareVal < match) min = guess + 1;
    else max = guess - 1;
  }

  return max;
};

const fitString = (ctx, str, maxWidth) => {
  let width = ctx.measureText(str).width;
  const ellipsis = '…';
  const ellipsisWidth = ctx.measureText(ellipsis).width;
  if (width <= maxWidth || width <= ellipsisWidth) {
    return str;
  }

  const index = binarySearch({
    max: str.length,
    getValue: guess => ctx.measureText(str.substring(0, guess)).width,
    match: maxWidth - ellipsisWidth,
  });

  return str.substring(0, index) + ellipsis;
};

export default Wheel
